// Configuration management for CTM Chat Widget
export class ConfigManager {
  constructor(logger) {
    this.logger = logger || console;
    // Agent availability data - can be set from widgetConfig or availability updates
    this.agentAvailability = {};

    this.config = {
      // Default config values
      headerText: "Chat with Us",
      welcomeMessage: "💬 Hey! How can we help you today?",
      engageMessage: "💬 Need help with anything?",
      suggestions: ["Talk to support", "Ask about pricing", "Learn more"],
      chatId: null,
      baseApiUrl: null,
      baseWsUrl: null,
      trackingHost: null,
      host: null,
      protocol: "https:",
      maxReconnectAttempts: 5,
      reconnectDelayMs: 3000,
      chatPrimaryColor: "#3b82f6",
      chatPrimaryColorDark: "#1e40af",
      chatBubbleSize: "64px",
      chatBubbleIconSize: "28px",
      chatWindowWidth: "340px",
      chatWindowHeight: "500px",
      iconText: null,
      iconImage: null,
      iconTexts: [],
      iconImages: [],
      engageMessageIcon: null,
      engageMessageTimer: 8,
      attachmentsEnabled: false,
      aiEnabled: false,
      singleButtonDefault: true,
      schedules: {},
      welcomeMessages: []
    };
  }

  // Get current configuration
  get() {
    return this.config;
  }

  // Update configuration with token data
  updateFromToken(token) {
    this.config.host = token.h;
    this.config.protocol = token.p;
    this.config.chatId = `${token.r}.${token.a}.${token.i}`;
    this.config.accountId = token.a;
    this.config.id = token.i;
    this.config.region = token.r;
    
    // Update base URLs after host is set
    this.updateBaseUrls();
    
    return this.config;
  }

  // Update base URLs from host
  updateBaseUrls() {
    if (!this.config.host) {
      this.config.baseApiUrl = null;
      this.config.baseWsUrl = null;
      return;
    }

    try {
      const pageProtocol = this.config.protocol || window.location.protocol;
      let hostLower = this.config.host.toLowerCase();
      
      if (!hostLower.startsWith("http://") && !hostLower.startsWith("https://")) {
        this.config.baseApiUrl = `${pageProtocol}//${this.config.host}`;
      } else {
        this.config.baseApiUrl = this.config.host;
      }

      // Derive WS URL from API URL
      const apiUrl = new URL(this.config.baseApiUrl);
      const wsProtocol = apiUrl.protocol === "https:" ? "wss:" : "ws:";
      this.config.baseWsUrl = `${wsProtocol}//${apiUrl.host}`;

    } catch (error) {
      this.logger.error("Error deriving base URLs from host:", error);
      this.config.baseApiUrl = null;
      this.config.baseWsUrl = null;
    }
  }

  // Merge fetched configuration
  merge(fetchedConfig, preserveKeys = ['chatId', 'host']) {
    // Store values to preserve
    const preserved = {};
    preserveKeys.forEach(key => {
      if (this.config[key]) {
        preserved[key] = this.config[key];
      }
    });

    // Merge configurations
    this.config = {
      ...this.config,
      ...fetchedConfig
    };

    // Restore preserved values
    Object.assign(this.config, preserved);

    return this.config;
  }

  // Fetch configuration from server
  async fetch({ cache = "default" } = {}) {
    if (!this.config.chatId || !this.config.baseApiUrl) {
      throw new Error("Configuration ID or Host missing.");
    }

    const configUrl = `${this.config.baseApiUrl}/api/chat-config/${encodeURIComponent(this.config.chatId)}`;

    try {
      const response = await fetch(configUrl, { mode: "cors", cache });
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Configuration not found for this widget ID.");
        } else {
          throw new Error(`Failed to fetch configuration (HTTP ${response.status}).`);
        }
      }

      const fetchedConfig = await response.json();
      
      // Handle schedule visibility
      if (fetchedConfig._scheduleVisibility) {
        // Process schedule visibility separately - don't store in main config
        const scheduleVisibility = fetchedConfig._scheduleVisibility;
        delete fetchedConfig._scheduleVisibility;
        
        return {
          config: fetchedConfig,
          scheduleVisibility
        };
      }

      return {
        config: fetchedConfig,
        scheduleVisibility: null
      };

    } catch (error) {
      this.logger.error("Error fetching configuration:", error);
      throw error;
    }
  }

  // Schedule evaluation methods
  evaluateScheduleCondition(scheduleId) {
    const schedule = this.config.schedules?.[scheduleId];
    if (!schedule) return false;

    const now = new Date();
    const timezone = schedule.timezone || "UTC";
    const offset = schedule.timezone_offset || 0;

    // Get current time in the schedule's timezone
    const localTime = new Date(now.getTime() + (offset * 1000));
    const currentDay = localTime.getDay(); // 0 = Sunday, 6 = Saturday
    const currentMinutes = localTime.getHours() * 60 + localTime.getMinutes();

    // Check if any time window matches
    for (const timeWindow of schedule.times || []) {
      const { start_time, end_time, days } = timeWindow;
      
      // Check if current day is active (days array: [Sun, Mon, Tue, Wed, Thu, Fri, Sat])
      if (!days[currentDay]) continue;

      // Check if current time is within the window
      if (currentMinutes >= start_time && currentMinutes <= end_time) {
        return true;
      }
    }

    // TODO: Handle schedule exceptions if needed

    return false;
  }

  evaluateUrlContainsCondition(value) {
  //  console.log("evaluateUrlContainsCondition:", value);
    const currentUrl = window.location.href.toLowerCase();
    return currentUrl.includes(value.toLowerCase());
  }

  // Set agent availability data (from widgetConfig or availability updates)
  setAgentAvailability(availability) {
    this.agentAvailability = availability || {};
  }

  // Get availability for a specific queue or default
  getAgentAvailability(queueId = null) {
    if (!this.agentAvailability || Object.keys(this.agentAvailability).length === 0) {
      return { available: true }; // Default to available if no data
    }
    if (queueId && this.agentAvailability[queueId]) {
      return this.agentAvailability[queueId];
    }
    // If no specific queue, return first available queue's data
    const firstQueue = Object.values(this.agentAvailability)[0];
    return firstQueue || { available: true };
  }

  /**
   * Evaluates agent_available condition
   * Returns true when agents are available to handle chats
   */
  evaluateAgentAvailableCondition(condition) {
    const queueId = condition.queue_id || null;
    const availability = this.getAgentAvailability(queueId);

    // If not available, condition fails
    if (!availability.available) {
      return false;
    }

    // Check minimum available time threshold if specified
    const minMinutes = condition.min_available_minutes || 0;
    if (minMinutes > 0 && availability.available_since) {
      const now = Date.now();
      // available_since is a Unix timestamp in seconds
      const availableSinceMs = availability.available_since * 1000;
      const minutesAvailable = (now - availableSinceMs) / 60000;
      return minutesAvailable >= minMinutes;
    }

    return true;
  }

  /**
   * Evaluates agent_unavailable condition
   * Returns true when NO agents are available to handle chats
   */
  evaluateAgentUnavailableCondition(condition) {
    const queueId = condition.queue_id || null;
    const availability = this.getAgentAvailability(queueId);

    // If agents are available, condition fails
    if (availability.available) {
      return false;
    }

    // Check minimum unavailable time threshold if specified
    const minMinutes = condition.min_unavailable_minutes || 0;
    if (minMinutes > 0 && availability.unavailable_since) {
      const now = Date.now();
      // unavailable_since is a Unix timestamp in seconds
      const unavailableSinceMs = availability.unavailable_since * 1000;
      const minutesUnavailable = (now - unavailableSinceMs) / 60000;
      return minutesUnavailable >= minMinutes;
    }

    return true;
  }

  /**
   * Returns the connector from this condition to the next condition.
   * Missing values remain implicit AND for existing published configs.
   * @param {Object} condition - Condition object.
   * @returns {string} Either "and" or "or".
   */
  conditionJoin(condition) {
    return condition?.match_join === "or" ? "or" : "and";
  }

  /**
   * Evaluates conditions using implicit AND, with per-condition OR connectors.
   * @param {Array} conditions - Array of condition objects.
   * @returns {boolean} True when the group passes.
   */
  evaluateConditions(conditions) {
    if (!conditions || !Array.isArray(conditions) || conditions.length === 0) {
      return true; // No conditions means always show
    }

    let currentAndGroupMet = true;

    for (let index = 0; index < conditions.length; index++) {
      const condition = conditions[index];
      let conditionMet = false;

      switch (condition.type) {
        case "schedule":
          // Schedule conditions are evaluated server-side only
          // If we see one here, it means server already approved it
          conditionMet = true;
          this.logger.debug("Schedule condition found on client - should have been filtered by server");
          break;
        case "url_contains":
          conditionMet = this.evaluateUrlContainsCondition(condition.value);
        //  console.log("evaluate:", condition, " -> ", conditionMet);
          break;
        case "agent_available":
          // Agent availability conditions are normally evaluated server-side
          // But we support client-side evaluation for real-time updates
          conditionMet = this.evaluateAgentAvailableCondition(condition);
          break;
        case "agent_unavailable":
          // Agent unavailability conditions are normally evaluated server-side
          // But we support client-side evaluation for real-time updates
          conditionMet = this.evaluateAgentUnavailableCondition(condition);
          break;
        // Add more condition types here as needed
        default:
          conditionMet = true; // Unknown conditions are ignored (treated as true)
      }

      currentAndGroupMet = currentAndGroupMet && conditionMet;

      if (this.conditionJoin(condition) === "or" || index === conditions.length - 1) {
        if (currentAndGroupMet) {
          return true;
        }
        currentAndGroupMet = true;
      }
    }

    return false;
  }

  isWithinSchedule(schedule) {
    const now = new Date();
    
    // Convert to schedule's timezone if needed
    const scheduleOffset = schedule.timezone_offset;
    if (typeof scheduleOffset === 'number') {
      // Adjust current time to schedule timezone
      const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
      now.setTime(utcTime + (scheduleOffset * 1000));
    }
    
    // Check if date is in exceptions (closed dates)
    if (schedule.exceptions && Array.isArray(schedule.exceptions)) {
      const nowTime = now.getTime();
      for (const exception of schedule.exceptions) {
        if (typeof exception === 'number') {
          // Unix timestamp in seconds
          const exceptionDate = new Date(exception * 1000);
          // Check if it's the same date (ignoring time)
          if (exceptionDate.toDateString() === now.toDateString()) {
            return false; // Closed on this date
          }
        }
      }
    }
    
    const currentDay = now.getDay(); // 0 = Sunday, 6 = Saturday
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    
    // Check if any time window matches
    if (schedule.times && Array.isArray(schedule.times)) {
      for (const timeWindow of schedule.times) {
        const { start_time, end_time, days } = timeWindow;
        
        // Check if current day is active
        if (days && days[currentDay]) {
          // Check if current time is within the window
          if (currentMinutes >= start_time && currentMinutes <= end_time) {
            return true;
          }
        }
      }
    }
    
    return false;
  }

  // Evaluate conditional welcome messages
  evaluateWelcomeMessage() {
    // Check if we have conditional welcome messages (already filtered by schedule on server)
    if (this.config.welcomeMessages && Array.isArray(this.config.welcomeMessages)) {
      
      // Iterate through welcome messages to find first matching condition
      for (const welcomeMsg of this.config.welcomeMessages) {
        // Server has already filtered by schedule, only URL conditions remain
        if (!welcomeMsg.conditions || welcomeMsg.conditions.length === 0) {
          // No conditions left after server filtering, this message applies
          return welcomeMsg.message;
        }
        
        // Evaluate remaining conditions (should only be URL-based)
        if (this.evaluateConditions(welcomeMsg.conditions)) {
          return welcomeMsg.message;
        }
      }
    }
    
    // Fall back to default welcome message
    return this.config.welcomeMessage || 'Welcome!';
  }

  // Filter suggestions based on conditions
  filterSuggestions(suggestions) {
    return suggestions.filter(suggestion => {
      if (typeof suggestion === "string") return true;
      if (typeof suggestion === "object" && suggestion.label) {
        const met = !suggestion.conditions ||
          this.evaluateConditions(suggestion.conditions);
      //  console.log("check conditions for:", suggestion, " -> ", met);
        return met
      }
      return false;
    });
  }
}
